import { postRequest } from './api';

/**
 * Dashboard Service API — manages persistent dashboard items stored in PostgreSQL.
 * Manager-service endpoints require Bearer-token auth.
 * SQL execution for refreshing data goes through the Data Service (no auth).
 */

const MANAGER_BASE = '/api/manager';

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handleResponse(response) {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed (${response.status})`);
  }
  return await response.json();
}

// ── Dashboard CRUD (multi-dashboard) ────────────────────────────────

export async function listDashboards() {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/list`, { headers: getAuthHeaders() })
  );
}

export async function getDashboard(dashboardId) {
  const url = dashboardId
    ? `${MANAGER_BASE}/api/dashboard/${dashboardId}`
    : `${MANAGER_BASE}/api/dashboard/`;
  return handleResponse(await fetch(url, { headers: getAuthHeaders() }));
}

export async function getDashboardByUuid(uuid) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/uuid/${uuid}`, { headers: getAuthHeaders() })
  );
}

export async function createDashboard(name, description = null) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name, description }),
    })
  );
}

export async function renameDashboard(dashboardId, name) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name }),
    })
  );
}

// Update arbitrary dashboard metadata fields (name, description, is_favorite,
// is_public, is_template, is_global, show_on_front_page). Only the keys
// present in `metadata` are sent to the server.
export async function updateDashboardMetadata(dashboardId, metadata) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(metadata),
    })
  );
}

export async function deleteDashboard(dashboardId) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    })
  );
}

export async function setDefaultDashboard(dashboardId) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/default`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    })
  );
}

// ── Dashboard Items ─────────────────────────────────────────────────

export async function addDashboardItem(item) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/items`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(item),
    })
  );
}

export async function updateDashboardLayout(layout, dashboardId) {
  const url = dashboardId
    ? `${MANAGER_BASE}/api/dashboard/${dashboardId}/layout`
    : `${MANAGER_BASE}/api/dashboard/layout`;
  return handleResponse(
    await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ layout }),
    })
  );
}

export async function removeDashboardItem(itemId) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/items/${itemId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    })
  );
}

export async function updateDashboardItem(itemId, updates) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/items/${itemId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    })
  );
}

// ── Data-service endpoint (execute saved SQL for dashboard refresh) ──

/**
 * Execute a saved SQL query for a dashboard item.
 *
 * Lambda proxy occasionally returns transient TLS / connection errors
 * which surface here as HTTP 5xx. The backend already retries those at
 * the helper layer, but if a 5xx still slips through we do one more
 * client-side retry with a short backoff so a flaky network blip
 * doesn't put a "500" tile on a freshly-added dashboard card.
 *
 * 4xx responses (bad SQL, etc.) are surfaced immediately — they're
 * deterministic and retrying them only delays the real error.
 */
export async function executeDashboardSQL(sql, limit = 1000, skipCache = false, options = {}) {
  const send = () => postRequest('data', '/execute-sql', { sql, limit, skip_cache: skipCache }, options);
  try {
    return await send();
  } catch (err) {
    // Retry once on transient 5xx (Lambda warmup, TLS hiccup, etc.).
    // The shared `postRequest` now puts the numeric status on `err.status`,
    // but older callsites used to match the message — handle both.
    const msg = String(err?.message || err);
    const status = Number(err?.status) || 0;
    const is5xx = (status >= 500 && status < 600) || /HTTP\s*5\d\d/.test(msg);
    if (!is5xx) throw err;
    await new Promise((r) => setTimeout(r, 600));
    return await send();
  }
}

// ── Dashboard Library endpoints ─────────────────────────────────────

export async function getMyDashboards() {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/library/my`, { headers: getAuthHeaders() })
  );
}

export async function getSharedDashboards() {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/library/shared`, { headers: getAuthHeaders() })
  );
}

export async function getPublicDashboards() {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/library/public`, { headers: getAuthHeaders() })
  );
}

export async function getRecentDashboards() {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/library/recent`, { headers: getAuthHeaders() })
  );
}

export async function getFavoriteDashboards() {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/library/favorites`, { headers: getAuthHeaders() })
  );
}

export async function getTemplateDashboards() {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/library/templates`, { headers: getAuthHeaders() })
  );
}

export async function getFrontPageDashboards() {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/library/front-page`, { headers: getAuthHeaders() })
  );
}

export async function getGlobalDashboards() {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/library/global`, { headers: getAuthHeaders() })
  );
}

// ── Favorite toggle ─────────────────────────────────────────────────

export async function toggleFavorite(dashboardId) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/favorite`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    })
  );
}

// ── Dashboard update (name, favorite, public) ───────────────────────

export async function updateDashboard(dashboardId, updates) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    })
  );
}

// ── Permissions / Sharing ───────────────────────────────────────────

export async function getDashboardPermissions(dashboardId) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/permissions`, { headers: getAuthHeaders() })
  );
}

export async function grantDashboardPermission(dashboardId, userId, permissionType) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/permissions/grant`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ user_id: userId, permission_type: permissionType }),
    })
  );
}

export async function updateDashboardPermission(dashboardId, userId, permissionType) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/permissions/update`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ user_id: userId, permission_type: permissionType }),
    })
  );
}

export async function revokeDashboardPermission(dashboardId, userId) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/permissions/revoke`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ user_id: userId, permission_type: 'read' }),
    })
  );
}

// ── User search for sharing dialog ──────────────────────────────────

export async function searchUsersForSharing(query = '') {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/users/search?q=${encodeURIComponent(query)}`, {
      headers: getAuthHeaders(),
    })
  );
}

// ── Clone dashboard ─────────────────────────────────────────────────

export async function cloneDashboard(dashboardId) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/clone`, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
  );
}

// ── Dashboard JSON export / import ──────────────────────────────────

export async function getDashboardJson(dashboardId) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/json`, { headers: getAuthHeaders() })
  );
}

export async function updateDashboardFromJson(dashboardId, jsonData) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/json`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(jsonData),
    })
  );
}


// ── Custom dashboard-level filter definitions ───────────────────────

export async function listDashboardFilters(dashboardId) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/filters`, { headers: getAuthHeaders() })
  );
}

export async function createDashboardFilter(dashboardId, definition) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/filters`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(definition),
    })
  );
}

export async function updateDashboardFilter(dashboardId, filterId, patch) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/filters/${filterId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(patch),
    })
  );
}

export async function deleteDashboardFilter(dashboardId, filterId) {
  return handleResponse(
    await fetch(`${MANAGER_BASE}/api/dashboard/${dashboardId}/filters/${filterId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    })
  );
}
